import React from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { SUBMISSION_CATEGORY, TASK_STATUS } from "common/constants";
import { SUBMISSION_CATEGORY_TO_LABEL } from "common/utils/submissions";
import { SubmissionType, useCreateTaskMutation } from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  InputRow,
  PrimaryButtons,
} from "../Common/__styles__/Modal";
import { Button } from "../Common/Button";
import Modal from "../Common/Modal";
import {
  Datepicker,
  Select,
  Text,
  Textarea,
  UserSelect,
} from "../Inputs/react-hook-form";
import {
  isAddressPanelProperty,
  isCustomMapGeometry,
  TargetObject,
} from "./types";

export const TASK_STATUS_OPTIONS = [
  {
    value: TASK_STATUS.TO_DO,
    label: "To do",
  },
  {
    value: TASK_STATUS.IN_PROGRESS,
    label: "In progress",
  },
  {
    value: TASK_STATUS.COMPLETE,
    label: "Complete",
  },
];

const makeAssociatedRecordTypeOptions = ({
  submissionTypes,
}: {
  submissionTypes: Array<Pick<SubmissionType, "id" | "name" | "category">>;
}) => {
  type Option = { value: string; label: string };

  const groups = new Map<SUBMISSION_CATEGORY, Array<Option>>();

  submissionTypes.forEach(type => {
    const group = groups.get(type.category);
    const entry: Option = { value: type.id, label: type.name };

    if (group) {
      group.push(entry);
    } else {
      groups.set(type.category, [entry]);
    }
  });

  const options: Array<{
    label: string;
    options: Array<Option>;
  }> = [];

  for (let [category, opts] of groups) {
    const label = SUBMISSION_CATEGORY_TO_LABEL[category];
    options.push({ label, options: opts });
  }

  return options;
};

export interface TaskModalHook {
  onSubmit: () => void;
  onCancel: () => void;
  targetObject: TargetObject;
}
export const useTaskModal = ({
  onSubmit,
  onCancel,
  targetObject,
}: TaskModalHook) => {
  const [showTaskModal, hideTaskModal] = useModal(
    () => (
      <Modal
        onRequestClose={() => {
          onCancel();
        }}
      >
        <TaskForm
          onSubmit={onSubmit}
          onCancel={onCancel}
          targetObject={targetObject}
        />
      </Modal>
    ),
    [onSubmit]
  );

  return [showTaskModal, hideTaskModal] as const;
};

const getLabel = (targetObject: TargetObject) => {
  if (!targetObject) return null;

  if (isAddressPanelProperty(targetObject)) {
    return targetObject.streetAddress;
  }

  if (isCustomMapGeometry(targetObject)) {
    return targetObject.label;
  }

  return null;
};

type TaskFormStructure = {
  name: string;
  assignees: {
    users: Array<string>;
  };
  status: TASK_STATUS;
  dueDate?: Maybe<string>;
  description: string;
  targetSubmissionTypeId?: Maybe<string>;
  propertyId?: string;
  customObjectId?: string;
};

const TaskForm = ({ onSubmit, onCancel, targetObject }: TaskModalHook) => {
  const { account } = React.useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<TaskFormStructure>({
    defaultValues: {
      status: TASK_STATUS.TO_DO,
      targetSubmissionTypeId: null,
      dueDate: null,
    },
  });

  const label = getLabel(targetObject);

  const [createTask, { loading }] = useCreateTaskMutation({
    onCompleted: () => {
      addSuccessToast(`Your task at ${label} was created.`);
      onSubmit();
    },
    onError: _ => {
      addErrorToast(`Failed to create task for ${label}.`);
    },
  });

  const associatedRecordTypeOptions = makeAssociatedRecordTypeOptions({
    submissionTypes: account?.submissionTypes ?? [],
  });

  const onFormSubmit = async (formData: TaskFormStructure) => {
    if (loading) return;

    // Transform the data to ensure assignees only has users
    const transformedData = {
      ...formData,
      assignees: {
        users: formData.assignees?.users || [],
      },
    };

    if (targetObject) {
      if (isAddressPanelProperty(targetObject)) {
        transformedData.propertyId = targetObject.id;
      }

      if (isCustomMapGeometry(targetObject)) {
        transformedData.customObjectId = targetObject.id;
      }
    }

    track("Task created", {
      label,
      propertyId: transformedData.propertyId,
      customObjectId: transformedData.customObjectId,
    });

    await createTask({ variables: { data: transformedData } });
  };

  return (
    <Container overflows>
      <HeaderSection>
        <h1>Add new task</h1>
        {label && <h2>{label}</h2>}
      </HeaderSection>
      <FormSection overflows>
        <ContentSection overflows>
          <InputRow>
            <Text
              {...register("name", {
                required: "Task name is required",
              })}
              error={errors.name?.message}
              label="Task name"
              required
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <UserSelect
              control={control}
              name="assignees"
              label="Assignees"
              includeUsers={true}
              includePublicUsers={false}
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Select
              control={control}
              name="status"
              label="Status"
              options={TASK_STATUS_OPTIONS}
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Datepicker control={control} name="dueDate" label="Due date" />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Textarea
              {...register("description")}
              error={errors.description?.message}
              label="Description"
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Select
              control={control}
              name="targetSubmissionTypeId"
              label="Associated record type"
              options={associatedRecordTypeOptions}
            />
          </InputRow>
        </ContentSection>
        <ButtonSection>
          <PrimaryButtons>
            <Button onClick={onCancel} size="medium" styleVariant="secondary">
              Cancel
            </Button>
            <Button
              disabled={loading}
              onClick={handleSubmit(onFormSubmit)}
              size="medium"
              styleVariant="primary"
              type="button"
            >
              Submit
            </Button>
          </PrimaryButtons>
        </ButtonSection>
      </FormSection>
    </Container>
  );
};

export default TaskForm;
